package com.shadowings.gero.presentation.component.quiz

import androidx.compose.foundation.Image
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.pager.PagerState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.MutableState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.draw.drawWithContent
import androidx.compose.ui.graphics.rememberGraphicsLayer
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.semantics.testTag
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.dp
import androidx.navigation.NavController
import androidx.navigation.compose.rememberNavController
import com.shadowings.gero.data.repository.InscriptionImages
import com.shadowings.gero.domain.model.IQuizContent
import com.shadowings.gero.domain.model.ImageKeyboardContent
import com.shadowings.gero.domain.model.LessonIds
import com.shadowings.gero.domain.model.QuizQuestionType
import com.shadowings.gero.domain.model.UserData
import com.shadowings.gero.domain.model.WrongAnswerRecord
import com.shadowings.gero.presentation.ImagePopup
import com.shadowings.gero.presentation.model.HeartActions
import com.shadowings.gero.presentation.model.QuizUiState
import com.shadowings.gero.presentation.screen.QuizScreenContent
import com.shadowings.gero.presentation.ui.AppTheme
import com.shadowings.gero.presentation.util.DeviceClass
import com.shadowings.gero.presentation.util.DevicePreviews
import com.shadowings.gero.presentation.util.rememberDeviceInfo
import compose.icons.FeatherIcons
import compose.icons.feathericons.ZoomIn
import gero.composeapp.generated.resources.Res
import gero.composeapp.generated.resources.chapter_fifteen_title
import gero.composeapp.generated.resources.question_type_transcribe
import gero.composeapp.generated.resources.share_quiz_transcribe
import org.jetbrains.compose.resources.painterResource
import org.jetbrains.compose.resources.stringResource

@DevicePreviews
@Preview
@Preview(name = "Dark", uiMode = 0x20, showBackground = true)
@Composable
private fun ImageKeyboardQuizPreview() {
    AppTheme {
        Surface {
            QuizScreenContent(
                uiState = QuizUiState(
                    lessonId = LessonIds.NOMI_IMPORTANTI,
                    quizList = listOf(
                        ImageKeyboardContent(
                            imageCode = "ab01",
                            reading = "mni".map { it.toString() },
                            translation = Res.string.chapter_fifteen_title
                        )
                    ),
                    userData = UserData()
                ),
                hiero = listOf(),
                onQuizCompleted = { _, _, _ -> },
                onAddExperience = {},
                heartActions = HeartActions(),
                navController = rememberNavController()
            )
        }
    }
}

@Composable
fun ImageKeyboardQuiz(
    quiz: ImageKeyboardContent,
    comboCount: MutableState<Int>,
    quizList: MutableState<List<IQuizContent>>,
    page: Int,
    testMode: Boolean,
    pagerState: PagerState,
    navController: NavController,
    quizResults: MutableState<Map<String, Boolean>>,
    errorsCount: MutableState<Int>,
    userData: UserData,
    lessonId: String = "",
    onComboFiveReached: (() -> Unit)? = null,
    onNextQuestion: ((isCorrect: Boolean) -> Unit)? = null,
    onAnswerResult: ((isCorrect: Boolean, record: WrongAnswerRecord) -> Unit)? = null
) {
    val state = rememberQuizPageState(
        quizId = quiz.id,
        page = page,
        pagerState = pagerState,
        navController = navController,
        quizResults = quizResults,
        quizList = quizList,
        comboCount = comboCount,
        errorsCount = errorsCount,
        onComboFiveReached = onComboFiveReached,
        onNextQuestion = onNextQuestion,
    )
    val graphicsLayer = rememberGraphicsLayer()

    var selectedLetters by rememberSaveable(quiz.id, stateSaver = StringListSaver) {
        mutableStateOf(listOf<String>())
    }
    val showFullscreenImage = remember { mutableStateOf(false) }
    val imageRes = remember(quiz.imageCode) { InscriptionImages.resFromCode(quiz.imageCode) }
    val deviceInfo = rememberDeviceInfo()
    val isLandscape = deviceInfo.isLandscape
    val reading = quiz.reading.joinToString("")

    Box(
        Modifier.fillMaxSize()
    ) {
        val questionContent = @Composable {
            Column(
                modifier = Modifier,
                horizontalAlignment = Alignment.CenterHorizontally,
                verticalArrangement = Arrangement.SpaceBetween
            ) {
                val imageBox = @Composable {
                    Box(
                        modifier = Modifier
                            .drawWithContent {
                                graphicsLayer.record {
                                    this@drawWithContent.drawContent()
                                }
                                drawContent()
                            },
                        contentAlignment = Alignment.Center
                    ) {
                        if (imageRes != null) {
                            Box {
                                Image(
                                    modifier = Modifier
                                        .heightIn(max = if (isLandscape) 110.dp else 160.dp)
                                        .clip(RoundedCornerShape(12.dp))
                                        .clickable { showFullscreenImage.value = true }
                                        .semantics { testTag = quiz.imageCode },
                                    painter = painterResource(imageRes),
                                    contentDescription = null,
                                    contentScale = ContentScale.Fit
                                )
                                Surface(
                                    modifier = Modifier
                                        .align(Alignment.BottomEnd)
                                        .padding(6.dp),
                                    shape = CircleShape,
                                    color = MaterialTheme.colorScheme.surface.copy(alpha = 0.7f)
                                ) {
                                    Icon(
                                        imageVector = FeatherIcons.ZoomIn,
                                        contentDescription = null,
                                        modifier = Modifier.padding(4.dp).size(16.dp)
                                    )
                                }
                            }
                            if (showFullscreenImage.value) {
                                ImagePopup(
                                    showFullscreenImage = showFullscreenImage,
                                    resource = imageRes,
                                    contentDescription = null,
                                    applyFilter = false
                                )
                            }
                        } else {
                            Text(quiz.imageCode)
                        }
                    }
                }
                val fontScale = LocalDensity.current.fontScale

                val isLandscapeAndBigFont = isLandscape && fontScale > 1.5f
                val isLandscapeAndSmallPhone = isLandscape && (
                        deviceInfo.deviceClass == DeviceClass.PHONE_SMALL
                                || deviceInfo.deviceClass == DeviceClass.PHONE_MEDIUM
                        )

                val noQuestion = isLandscapeAndSmallPhone || isLandscapeAndBigFont

                QuestionBox(
                    question = if (noQuestion) "" else stringResource(Res.string.question_type_transcribe),
                    questionTestTag = "image_keyboard_quiz",
                    wordContent = imageBox
                )

                Spacer(Modifier.height(16.dp))

                PhoneticAnswerSlots(
                    reading = quiz.reading,
                    selectedLetters = selectedLetters,
                    showResult = state.showResult,
                    blinking = state.blinking,
                    isLandscape = isLandscape
                )
            }
        }

        val keyboardContent = @Composable {
            PhoneticKeyboardInput(
                reading = quiz.reading,
                selectedLetters = selectedLetters,
                onSelectedLettersChange = { selectedLetters = it },
                enabled = !state.showResult,
                testMode = testMode,
                isLandscape = isLandscape
            )
        }

        QuizContentScaffold(
            testTag = "image_keyboard_content",
            isLandscape = isLandscape,
            landscapeOptionsWeight = 1.2f,
            landscapeOptionsArrangement = Arrangement.Center,
            landscapeOptionsHorizontalAlignment = Alignment.CenterHorizontally,
            landscapeOptionsScrollable = false,
            portraitOptionsArrangement = Arrangement.spacedBy(8.dp, Alignment.Bottom),
            questionContent = { questionContent() },
            optionsContent = { keyboardContent() }
        )

        val info = quiz.translation?.let { "$reading -> ${stringResource(it)}" } ?: ""
        val quizInfo = """
            Quiz ID: ${quiz.id}
            Type: Image Keyboard Quiz
            Image: ${quiz.imageCode}
            Reading (Answer): $reading
            Info: $info
        """.trimIndent()
        val userAnswer = selectedLetters.joinToString("")

        val shareText = stringResource(Res.string.share_quiz_transcribe)

        QuizBottomFeedbackAndButton(
            modifier = Modifier.align(Alignment.BottomCenter),
            state = state,
            buttonEnabled = selectedLetters.size == quiz.reading.size,
            info = info,
            correctAnswer = reading,
            userData = userData,
            lessonId = lessonId,
            quizInfo = quizInfo,
            userAnswer = userAnswer,
            shareText = shareText,
            shareImageProvider = {
                try {
                    graphicsLayer.toImageBitmap()
                } catch (e: Exception) {
                    null
                }
            },
            onConfirmClick = {
                val isCorrect = userAnswer.equals(reading, ignoreCase = true)
                onAnswerResult?.invoke(
                    isCorrect,
                    WrongAnswerRecord(
                        id = WrongAnswerRecord.buildId(lessonId, quiz.imageCode),
                        lessonId = lessonId,
                        type = QuizQuestionType.IMAGE_KEYBOARD,
                        questionText = quiz.imageCode,
                        correctAnswerText = reading,
                        userAnswerText = userAnswer,
                        hieroglyphs = ""
                    )
                )
                isCorrect
            }
        )
    }
}
